import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { FtoReportContext } from '../novelty-check-report-context';
import type { FtoReportState } from '../novelty-check-report-state';
import { Logger } from '@patsnap/tahiti-core';
import { FtoTaskStep } from '@/domains/fto-report/enums';
import { FtoReportFeatureIdentify } from '../../feature-identify';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { FtoReportTaskStatusModel } from '@/domains/fto-report/models/task-status-model';
import { FtoReportTaskStatusService } from '../../task-status-service';
import type { FeatureIdentify } from '@/domains/novelty-check-report/services/feature-identify';

@injectable()
export class FtoReportTechFeaturePullStartState implements FtoReportState {
    // eslint-disable-next-line max-params
    constructor(
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(FtoReportFeatureIdentify)
        private readonly _featureIdentify: FeatureIdentify,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(FtoReportTaskStatusModel)
        private readonly _noveltyCheckTaskStatusModel: FtoReportTaskStatusModel,
        @inject(FtoReportTaskStatusService)
        private readonly _noveltyCheckTaskStatusService: FtoReportTaskStatusService,
    ) { }

    getStep(): FtoTaskStep {
        return FtoTaskStep.TECH_FEATURE_PULL_START;
    }

    async enter(context: FtoReportContext): Promise<void> {
        this._logger.info('进入<技术特征识别ing>状态');
        // 切换任务到技术特征识别开始状态
        this._switchTaskStep(FtoTaskStep.TECH_FEATURE_PULL_START);
        // 识别技术特征
        return this._recognizeTechFeature()
            .then(async () => {
                // 自动进入技术特征识别成功状态
                const state = context.getState(FtoTaskStep.TECH_FEATURE_PULL_SUCCESS);
                await context.transitionTo(state);
                await this._noveltyCheckTaskStatusService.pullTaskStatus();
            })
            .catch(() => {
                // 切换任务到技术特征识别结束状态
                this._switchTaskStep(FtoTaskStep.FTO_SEARCH_INPUT);
                // 自动进入技术特征识别失败状态
                const state = context.getState(FtoTaskStep.FTO_SEARCH_INPUT);
                context.transitionTo(state);
            });
    }

    /**
     * 识别技术特征
     */
    private _recognizeTechFeature() {
        const taskId = this._currentTaskService.taskId;
        return this._featureIdentify.recognize(taskId);
    }

    /**
     * 切换任务步骤
     * @param step 目标步骤
     */
    private _switchTaskStep(step: FtoTaskStep) {
        this._noveltyCheckTaskStatusModel.update(state => {
            state.taskStep = step;
            return state;
        });
    }

    async exit(): Promise<void> {
        this._logger.info('离开<技术特征识别ing>状态');
    }

    async handleNext(context: FtoReportContext): Promise<void> {
        const state = context.getState(FtoTaskStep.TECH_FEATURE_PULL_SUCCESS);
        await context.transitionTo(state);
    }

    async handlePrevious(): Promise<void> {
        this._logger.info('<技术特征识别ing>状态没有上一步');
    }
}
